import MaterialTable from "@material-table/core";
import {
  Card,
  TextField,
  Box,
  InputAdornment,
  Button,
  IconButton,
  Popover,
  MenuItem,
  Tooltip,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SearchIcon from "@mui/icons-material/Search";
import { useMemo, useState, useEffect, useRef } from "react";
import { useInput, useRecordContext, useRefresh } from "react-admin";
import { UploadDialog } from "./components/UploadVoucherCodeDialog";
import Add from "@mui/icons-material/Add";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import GridOnIcon from "@mui/icons-material/GridOn";
import { RewardCode } from "./components/AddRewardCodes";
import { useRewardCodesQuery, useRewardMutation } from "./controller";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

interface RewardCodeTableProps {
  formData: any;
  setTabValue: (value: string) => void;
}

const RewardCodeTable: React.FC<RewardCodeTableProps> = ({ setTabValue }) => {
  const navigate = useNavigate();
  const record = useRecordContext();
  const refresh = useRefresh();
  const { field } = useInput({ source: "item_detail.codes" });
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [openRewardCode, setOpenRewardCode] = useState(false);
  const [exportAnchorPos, setExportAnchorPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const exportBtnRef = useRef<HTMLButtonElement | null>(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Search state
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");

  // Active/Not Active filter (mirrors ListPage behavior)
  // null = no filter, "ACTIVE" => status = PURCHASED, "NOT_ACTIVE" => status IN (REDEEMED, EXPIRED)
  const [activeStatus, setActiveStatus] = useState<
    "ACTIVE" | "NOT_ACTIVE" | null
  >(null);

  // Debounce search text to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchText]);

  // Reset page to 0 when search changes
  useEffect(() => {
    setPage(0);
  }, [debouncedSearchText]);

  // Reset page to 0 when active/not active filter changes
  useEffect(() => {
    setPage(0);
  }, [activeStatus]);

  // Create a separate query key for pagination
  const [queryParams, setQueryParams] = useState({
    where: {
      reward_catalog_id: { _eq: record?.id },
    },
    orderBy: [{ created_at: "desc" }],
    limit: pageSize,
    offset: page * pageSize,
  });

  // Update query params when pagination or search changes
  useEffect(() => {
    const baseWhere: any = {
      reward_catalog_id: { _eq: record?.id },
    };

    const andFilters: any[] = [baseWhere];

    // Add search conditions if debouncedSearchText is provided
    if (debouncedSearchText) {
      andFilters.push({
        _or: [
          { code: { _ilike: `%${debouncedSearchText}%` } },
          { label_name: { _ilike: `%${debouncedSearchText}%` } },
        ],
      });
    }

    // Add active/not active status filter (same mapping as ListPage)
    if (activeStatus === "ACTIVE") {
      andFilters.push({ status: { _eq: "AVAILABLE" } });
    } else if (activeStatus === "NOT_ACTIVE") {
      andFilters.push({ status: { _neq: "AVAILABLE" } });
    }

    const whereCondition =
      andFilters.length > 1 ? { _and: andFilters } : baseWhere;

    setQueryParams({
      where: whereCondition,
      orderBy: [{ created_at: "desc" }],
      limit: pageSize,
      offset: page * pageSize,
    });
  }, [page, pageSize, record?.id, debouncedSearchText, activeStatus]);

  // Fetch reward codes filtered by reward_catalog_id with pagination
  // Prevent executing the query when record is not yet available
  const { data, isLoading, error } = useRewardCodesQuery(queryParams, {
    enabled: !!record?.id,
  });

  // Get mutation functions
  const { deleteRewardAsync, updateRewardAsync, exportRewardCode } =
    useRewardMutation();

  // Use the fetched data or fallback to field value
  const recordData = data?.reward_codes || [];
  const totalCount = data?.reward_codes_aggregate?.aggregate?.count || 0;

  const COLUMNS = useMemo(
    () => [
      {
        field: "code",
        title: "Code",
        editComponent: (props: any) => {
          return (
            <TextField
              {...props}
              variant="standard"
              value={props.value ?? ""}
              onChange={(e) => {
                const digitsOnly = e.target.value.replace(/\D/g, "");
                props.onChange(digitsOnly);
              }}
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
              onKeyDown={(e) => {
                // Allow navigation and control keys
                const allowed = [
                  "Backspace",
                  "Delete",
                  "ArrowLeft",
                  "ArrowRight",
                  "Tab",
                  "Home",
                  "End",
                  "Enter",
                  "Escape",
                ];
                if (allowed.includes(e.key) || e.ctrlKey || e.metaKey) return;
                // Allow digits 0-9
                if (/^[0-9]$/.test(e.key)) return;
                e.preventDefault();
              }}
            />
          );
        },
      },
      {
        field: "label_name",
        title: "Names",
        editable: "never" as const,
      },
      {
        field: "created_at",
        title: "Date of Input",
        editable: "never" as const,
        render: (rowData: any) => {
          return rowData.created_at
            ? new Date(rowData.created_at).toLocaleDateString()
            : "";
        },
      },
      {
        field: "status",
        title: "Status",
        editable: "never" as const,
      },
    ],
    []
  );

  const editTableObj = {
    // Disable editing/deleting when row status is not AVAILABLE
    isEditable: (rowData: any) => {
      // block edit if purchased or status not AVAILABLE
      return !rowData?.purchased && rowData?.status === "AVAILABLE";
    },
    isDeletable: (rowData: any) => {
      // block delete if purchased or status not AVAILABLE
      return !rowData?.purchased && rowData?.status === "AVAILABLE";
    },
    onRowUpdate: async (newData: any, oldData: any) => {
      // Ask for confirmation before applying the update
      if (newData?.code === oldData?.code) {
        // nothing changed; skip update
        return;
      }

      const result = await Swal.fire({
        title: "Confirm update",
        html: `Update code from <b>${oldData?.code}</b> to <b>${newData?.code}</b>?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, update",
        cancelButtonText: "Cancel",
        reverseButtons: true,
        focusCancel: true,
      });

      if (!result.isConfirmed) {
        // User cancelled; do nothing
        return;
      }

      try {
        // Prepare the update data (only include fields that can be updated)
        const updateData = {
          code: newData.code,
        };

        // Call the update mutation with the record ID
        await updateRewardAsync(oldData.id, updateData);

        // Also update the local state for immediate UI feedback
        const dataUpdate = [...recordData];
        const target = dataUpdate.find((el) => el.code === oldData.code);
        const index = dataUpdate.indexOf(target);
        if (index > -1) {
          dataUpdate[index] = newData;
          field.onChange(dataUpdate);
        }
      } catch (error) {
        console.error("Error updating reward code:", error);
        // The error notification is already handled in the mutation
      }
    },
    onRowDelete: async (oldData: any) => {
      // Ask for confirmation before deleting
      const result = await Swal.fire({
        title: "Delete this code?",
        html: `This will permanently remove <b>${oldData?.code}</b>.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete",
        cancelButtonText: "Cancel",
        reverseButtons: true,
        focusCancel: true,
      });

      if (!result.isConfirmed) return; // user cancelled

      try {
        // Call the delete mutation with the record ID
        await deleteRewardAsync(oldData.id);

        // Also update the local state for immediate UI feedback
        const dataDelete = [...recordData];
        const target = dataDelete.find((el) => el.code === oldData.code);
        const index = dataDelete.indexOf(target);
        if (index > -1) {
          dataDelete.splice(index, 1);
          field.onChange(dataDelete);
        }
        refresh();
      } catch (error) {
        console.error("Error deleting reward code:", error);
        // The error notification is already handled in the mutation
      }
    },

    // ketika data purchased true maka edit dan delete disable
    isEditHidden: (data: any) => {
      return data.purchased || data?.status !== "AVAILABLE";
    },
    isDeleteHidden: (data: any) => {
      return data.purchased || data?.status !== "AVAILABLE";
    },
  };

  // Show loading state
  if (isLoading) {
    return (
      <Card variant="outlined" style={{ padding: "20px", textAlign: "center" }}>
        <div>Loading reward codes...</div>
      </Card>
    );
  }

  // Show error state
  if (error) {
    return (
      <Card variant="outlined" style={{ padding: "20px", textAlign: "center" }}>
        <div style={{ color: "red" }}>
          Error loading reward codes:{" "}
          {(error as Error)?.message || "Unknown error"}
        </div>
      </Card>
    );
  }

  if (!record || !record.id) {
    Swal.fire({
      icon: "info",
      title: "Important",
      text: "Please Save this Config first",
    }).then(() => {
      setTabValue("1");
    });
  }

  const handleExportCode = (exportType: "pdf" | "xlsx" = "pdf") => {
    if (!record?.id) {
      Swal.fire({
        icon: "info",
        title: "Important",
        text: "Please Save this Config first",
      }).then(() => setTabValue("1"));
      return;
    }

    // Build input for export using current filters
    const input: any = {
      type: "reward-barcode",
      data: {
        filter: {
          reward_catalog_id: record.id,
        },
        export_type: exportType,
      },
    };
    exportRewardCode.mutate(
      { input },
      {
        onSuccess: (data: any) => {
          if (data?.report?.status) {
            Swal.fire({
              icon: "success",
              title: "Export started",
              html: "Your report is being generated in the background.<br/>You can view and download it from the <b>Reports</b> page.",
              showCancelButton: true,
              confirmButtonText: "Go to Reports",
              cancelButtonText: "Stay here",
            }).then((res) => {
              if (res.isConfirmed) {
                navigate("/reports");
              }
            });
          }
        },
      }
    );
  };

  const handleOpenExportMenu = () => {
    const btn = exportBtnRef.current;
    if (btn) {
      const rect = btn.getBoundingClientRect();
      setExportAnchorPos({
        top: Math.round(rect.bottom + window.scrollY),
        left: Math.round(rect.left + window.scrollX),
      });
    }
  };

  const handleCloseExportMenu = () => setExportAnchorPos(null);

  const handleChooseExport = (type: "pdf" | "xlsx") => {
    handleExportCode(type);
    handleCloseExportMenu();
  };

  return (
    <>
      <MaterialTable
        data={recordData}
        columns={COLUMNS}
        title={""}
        components={{
          Container: (props) => {
            return <Card variant="outlined">{props.children}</Card>;
          },
          Toolbar: (props) => {
            return (
              <>
                <Box
                  sx={{ mb: 2, ml: 2, display: "flex", alignItems: "center" }}
                >
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="search code"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ maxWidth: 200 }}
                  />
                  {/* Active / Not Active filters */}
                  <Box sx={{ ml: 1, display: "flex", gap: 1 }}>
                    <Button
                      variant={
                        activeStatus === "ACTIVE" ? "contained" : "outlined"
                      }
                      color="primary"
                      size="small"
                      onClick={() =>
                        setActiveStatus((prev) =>
                          prev === "ACTIVE" ? null : "ACTIVE"
                        )
                      }
                    >
                      Active
                    </Button>
                    <Button
                      variant={
                        activeStatus === "NOT_ACTIVE" ? "contained" : "outlined"
                      }
                      color="primary"
                      size="small"
                      onClick={() =>
                        setActiveStatus((prev) =>
                          prev === "NOT_ACTIVE" ? null : "NOT_ACTIVE"
                        )
                      }
                    >
                      Not Active
                    </Button>
                  </Box>
                  <Box sx={{ flexGrow: 1 }} />
                  <Tooltip title="Export Reward Codes">
                    <IconButton
                      onClick={handleOpenExportMenu}
                      ref={exportBtnRef}
                    >
                      <CloudDownloadIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Batch Upload Reward Codes">
                    <IconButton onClick={() => setOpenUploadDialog(true)}>
                      <CloudUploadIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Add New Reward Codes">
                    <IconButton onClick={() => setOpenRewardCode(true)}>
                      <Add />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box>
                  {searchText && (
                    <Box
                      sx={{
                        mt: 0.5,
                        ml: 2,
                        color: "text.secondary",
                        fontSize: "0.875rem",
                      }}
                    >
                      {totalCount === 0
                        ? `No results found for "${searchText}"`
                        : `Found ${totalCount} result(s) for "${searchText}"`}
                    </Box>
                  )}
                </Box>
              </>
            );
          },
        }}
        editable={{ ...editTableObj }}
        options={{
          emptyRowsWhenPaging: false,
          search: false,
          paging: true,
          pageSize: pageSize,
          pageSizeOptions: [5, 10, 20, 50],
        }}
        page={page}
        totalCount={totalCount}
        onPageChange={(newPage) => {
          setPage(newPage);
        }}
        onRowsPerPageChange={(newPageSize) => {
          setPageSize(newPageSize);
          setPage(0); // Reset to first page
        }}
        localization={{
          body: {
            emptyDataSourceMessage: record?.id
              ? `No reward codes found for catalog ID: ${record.id}`
              : "No reward codes available",
          },
        }}
        actions={[
          {
            icon: () => <CloudUploadIcon />,
            tooltip: "Upload batch",
            isFreeAction: true,
            onClick: () => {
              setOpenUploadDialog(true);
            },
          },
          {
            icon: () => <Add />,
            tooltip: "Add",
            isFreeAction: true,
            onClick: () => {
              setOpenRewardCode(true);
            },
          },
        ]}
      />
      <UploadDialog
        open={openUploadDialog}
        onClose={() => setOpenUploadDialog(false)}
      />
      <RewardCode
        open={openRewardCode}
        onClose={() => setOpenRewardCode(false)}
      />

      <Popover
        open={Boolean(exportAnchorPos)}
        anchorReference="anchorPosition"
        anchorPosition={exportAnchorPos || undefined}
        onClose={handleCloseExportMenu}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <MenuItem onClick={() => handleChooseExport("pdf")}>
          <PictureAsPdfIcon fontSize="small" style={{ marginRight: 8 }} />
          Export as PDF
        </MenuItem>
        <MenuItem onClick={() => handleChooseExport("xlsx")}>
          <GridOnIcon fontSize="small" style={{ marginRight: 8 }} />
          Export as XLSX
        </MenuItem>
      </Popover>
    </>
  );
};

export default RewardCodeTable;
